import { isObject } from '@repo/utils/type.utils';

import type { CustomerInfo, MetaData } from './quotation.types';

export interface AssetRetrievalQuotation {
    buCode: string;
    countryCode: string;
    createdAt: string;
    createdBy: string;
    customerIdentifier?: CustomerInfo;
    lastModifiedAt: string;
    metaData: MetaData;
    name: string;
    quotationId: string;
    status: string;
    validTill: string;
    versions: Array<{
        version: number;
        createdAt: string;
    }>;
}

export interface AssetRetrievalOrder {
    buCode: string;
    canAddAttachments: boolean;
    canAddReceipt: boolean;
    canAddService: boolean;
    canCancelOrder: boolean;
    canConvertToDeliveryOrder: boolean;
    canConvertToOrder: boolean;
    canEdit: boolean;
    canModifyDiscount: boolean;
    canPayOnline: boolean;
    canPrintTemsAndCondtion: boolean;
    canViewOrder: boolean;
    cashAndCarryOrder: boolean;
    canyPayInIpos: boolean;
    comments: unknown[];
    creationMethod: string;
    customer: Customer;
    customerBilling?: Customer;
    discounts: unknown[]; // TODO: Add definition when required
    hasChallengedPayment: boolean;
    hasReceiptItems: boolean;
    hasReviewOrder: boolean;
    hasService: boolean;
    hasTermsOfCondition: boolean;
    id: string;
    isCancelled: boolean;
    isCashLineOrder: boolean;
    isCcdService: boolean;
    isCompleted: boolean;
    isPaid: boolean;
    isPaymentOnDelivery: boolean;
    isRefundOrder: boolean;
    isSalesDeskMode: boolean;
    isTransportOrder: boolean;
    orderAlias: string;
    orderCreationDate: string;
    orderCurrencyCode: string;
    orderIsPartiallyPaid: boolean;
    orderItems: unknown[]; // TODO: Add definition when required
    orderNo: string;
    orderNoSource: string;
    orderPaid: boolean;
    orderStatus: string;
    orderType: string;
    orderVersion: 0;
    payOrderLink: '';
    pickupNotification: PickupNotification[];
    proposal: Record<string, unknown>; // TODO: Add definition when required
    totalItemPrice: TotalPrice;
    totalPrice: TotalPrice;
    type: string;
    version: number;
}

interface Customer {
    addressLine1: string;
    businessName: string;
    city: string;
    countryCode: string;
    email: string;
    firstName: string;
    foreignAddressCountryCode: string;
    isAddedBillAddress: boolean;
    isAddedDeliveryAddress: boolean;
    isBusinessCustomer: boolean;
    isNewDeliveryAddress: boolean;
    landlinePhone: string;
    lastName: string;
    mobilePhone: string;
    noOfBusinessCards: number;
    noOfFamilyCards: number;
    partyId: string;
    phoneticName: string;
    preferredLocale: string;
    vatPayer: string;
    zipCode: string;
}

interface TotalPrice {
    currencyCode: string;
    priceExclTax?: number;
    priceInclTax: number;
}

interface PickupNotification {
    contactMethodData: string;
    contactMethodType: string;
    type: string;
}

export interface AssetRetrievalDraft {
    buCode: string;
    countryCode: string;
    createdAt: string;
    createdBy: string;
    customerInfo?: AssetRetrievalDraftCustomerInfo;
    expiresOn: string;
    lastModifiedAt: string;
    metaData: AssetRetrievalDraftMetaData;
    status: string;
}

type AssetRetrievalDraftApiResponse = Record<string, AssetRetrievalDraft>;

interface AssetRetrievalDraftCustomerInfo {
    id: string;
    email: string;
}

interface AssetRetrievalDraftMetaData {
    imageUrls: string[];
    numberOfPackages: number;
    totalItemCount: number;
    orderCreationDate: string;
    totalPrice: TotalPrice;
}

export type AssetRetrievalType = AssetRetrievalOrder | AssetRetrievalDraft | AssetRetrievalQuotation;

export const isAssetRetrievalQuotation = (value: unknown): value is AssetRetrievalQuotation =>
    isObject(value) && 'quotationId' in value;

export const isAssetRetrievalOrder = (value: unknown): value is AssetRetrievalOrder =>
    isObject(value) && 'orderNo' in value && 'orderType' in value;

const isAssetRetrievalDraft = (value: unknown): value is AssetRetrievalDraft =>
    isObject(value) && 'buCode' in value && !('orderNo' in value) && !('quotationId' in value);

export const isAssetRetrievalDraftResponse = (
    value: unknown,
    draftId: string
): value is AssetRetrievalDraftApiResponse => isObject(value) && isAssetRetrievalDraft(value[draftId]);
